import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Truck, MapPin, Phone, User, CalendarDays, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { retailerPickups, createPickup, cancelPickup } from "../../api";
import { PickupTracker, StatusPill } from "./PickupTracker";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => { try { return new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };
const SLOTS = ["10:00 AM - 1:00 PM", "1:00 PM - 4:00 PM", "4:00 PM - 7:00 PM"];
const today = () => new Date().toISOString().slice(0, 10);
const input = "w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#0EA5E9]";

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#64748B] mb-1">{label}</label>
    <div className="relative">
      {Icon && <Icon size={14} className="absolute left-3 top-[15px] text-slate-400" />}
      {children}
    </div>
  </div>
);

const PickupCard = ({ p, onCancel }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-100 rounded-2xl p-5" data-testid={`pickup-card-${p.ref}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-xs text-[#94A3B8] font-semibold">{p.ref} · {fmt(p.created_at)}</div>
          <div className="text-xl font-extrabold text-[#0D1B2A]">{inr(p.collected_amount ?? p.amount)}</div>
        </div>
        <StatusPill status={p.status} testId={`pickup-status-${p.ref}`} />
      </div>
      <PickupTracker pickup={p} />
      {p.agent_name && (
        <div className="mt-4 text-sm bg-[#F0FDFA] rounded-xl px-4 py-3 text-[#0D1B2A]" data-testid={`pickup-agent-${p.ref}`}>
          Agent: <b>{p.agent_name}</b> · {p.agent_mobile} · {p.scheduled_date}
        </div>
      )}
      <div className="mt-4 flex items-center justify-between">
        <button onClick={() => setOpen(!open)} data-testid={`pickup-toggle-${p.ref}`} className="text-xs font-semibold text-[#0EA5E9] flex items-center gap-1">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Activity log
        </button>
        {p.status === "requested" && (
          <button onClick={() => onCancel(p)} data-testid={`pickup-cancel-${p.ref}`} className="text-xs font-semibold text-red-500 hover:underline">Cancel request</button>
        )}
      </div>
      {open && (
        <ul className="mt-3 space-y-2 border-l-2 border-slate-100 pl-4">
          {(p.timeline || []).slice().reverse().map((t, i) => (
            <li key={i} className="text-xs"><span className="font-semibold text-[#0D1B2A]">{t.note}</span> <span className="text-[#94A3B8]">· {fmt(t.at)}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const RetailerCashPickup = ({ me, onWalletChange }) => {
  const { toast } = useToast();
  const blank = () => ({
    amount: "", contact_name: me?.full_name || "", contact_mobile: me?.phone || "",
    pickup_address: [me?.address, me?.city, me?.state, me?.pincode].filter(Boolean).join(", "),
    preferred_date: today(), preferred_slot: SLOTS[0], remarks: "",
  });
  const [form, setForm] = useState(blank);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = useCallback(() => { retailerPickups().then((d) => { setItems(d); onWalletChange?.(); }).catch(() => {}); }, [onWalletChange]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast({ title: "Enter a valid amount" });
    if (!/^[6-9]\d{9}$/.test(form.contact_mobile)) return toast({ title: "Enter a valid 10-digit mobile" });
    if (form.pickup_address.trim().length < 5) return toast({ title: "Enter the pickup address" });
    setSaving(true);
    try {
      const p = await createPickup({ ...form, amount: amt });
      toast({ title: "Pickup requested", description: `Ref ${p.ref} · ${inr(amt)}` });
      setForm(blank());
      load();
    } catch (err) {
      toast({ title: "Request failed", description: err?.response?.data?.detail?.toString() || "Please try again." });
    } finally { setSaving(false); }
  };

  const doCancel = async () => {
    try { await cancelPickup(confirming.id); toast({ title: "Request cancelled" }); load(); }
    catch (err) { toast({ title: "Could not cancel", description: err?.response?.data?.detail || "" }); }
    setConfirming(null);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="cash-pickup-section">
      <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4 h-fit" data-testid="pickup-form">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-[#ECFDF5] text-[#06B6D4] flex items-center justify-center"><Truck size={20} /></div>
          <div><h2 className="font-bold text-[#0D1B2A]">Request Cash Pickup</h2><p className="text-xs text-[#64748B]">Our agent collects cash from your shop & credits your wallet.</p></div>
        </div>
        <Field label="Cash amount (₹)"><input type="number" min="1" step="0.01" placeholder="e.g. 25000" value={form.amount} onChange={set("amount")} className={input} data-testid="pickup-amount-input" /></Field>
        <Field label="Contact person" icon={User}><input placeholder="Name" value={form.contact_name} onChange={set("contact_name")} className={`${input} pl-9`} data-testid="pickup-contact-name-input" /></Field>
        <Field label="Contact mobile" icon={Phone}><input placeholder="10-digit mobile" maxLength={10} value={form.contact_mobile} onChange={set("contact_mobile")} className={`${input} pl-9`} data-testid="pickup-contact-mobile-input" /></Field>
        <Field label="Pickup address" icon={MapPin}><textarea rows={2} placeholder="Shop address" value={form.pickup_address} onChange={set("pickup_address")} className={`${input} pl-9`} data-testid="pickup-address-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Preferred date" icon={CalendarDays}><input type="date" min={today()} value={form.preferred_date} onChange={set("preferred_date")} className={`${input} pl-9`} data-testid="pickup-date-input" /></Field>
          <Field label="Time slot">
            <select value={form.preferred_slot} onChange={set("preferred_slot")} className={input} data-testid="pickup-slot-select">
              {SLOTS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Remarks (optional)"><input placeholder="Any note for the agent" value={form.remarks} onChange={set("remarks")} className={input} data-testid="pickup-remarks-input" /></Field>
        <button disabled={saving} data-testid="pickup-submit-button" className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-full disabled:opacity-60" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
          {saving && <Loader2 size={16} className="animate-spin" />} Request Pickup
        </button>
      </form>

      <div className="lg:col-span-3 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <h2 className="font-bold text-[#0D1B2A] mb-4">Track Pickups</h2>
        <div className="space-y-4" data-testid="pickup-list">
          {items.length === 0 && <div className="py-12 text-center text-sm text-[#64748B]" data-testid="pickup-empty">No pickup requests yet.</div>}
          {items.map((p) => <PickupCard key={p.id} p={p} onCancel={setConfirming} />)}
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pickup-cancel-dialog">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-[#0D1B2A] mb-2">Cancel pickup {confirming.ref}?</h3>
            <p className="text-sm text-[#64748B] mb-5">This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(null)} className="px-4 py-2 rounded-full text-sm font-semibold bg-slate-100" data-testid="pickup-cancel-dismiss">Keep</button>
              <button onClick={doCancel} className="px-4 py-2 rounded-full text-sm font-semibold bg-red-500 text-white" data-testid="pickup-cancel-confirm">Yes, cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
